import React, { KeyboardEvent } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Form, Input, Divider, Segmented } from 'antd';
import { GoogleLogin } from '@react-oauth/google';
import { BeatLoader } from 'react-spinners';
import {
    AiOutlineMail,
    AiOutlinePhone,
    AiOutlineLock,
} from 'react-icons/ai';
import { useTranslation } from 'next-i18next';

import { useLoginLogic, LoginFormValues } from '../../hooks/useLoginLogic';

import styles from './style.module.scss';

// ============================================================================
// Component
// ============================================================================

const LoginForm: React.FC = () => {
    const { t } = useTranslation('common');
    const {
        segmentValue,
        setSegmentValue,
        isSubmitting,
        isGoogleLoading,
        passwordInputRef,
        handleLoginSubmit,
        handleGoogleLoginSuccess,
        handleGoogleLoginError,
        handleTelegramLogin,
        getLoginLink,
    } = useLoginLogic();

    // ========================================================================
    // UI Handlers
    // ========================================================================

    const handleEmailKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            e.preventDefault();
            passwordInputRef.current?.focus();
        }
    };

    const handlePhoneKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        // Allow only numbers, backspace, delete, and enter
        const allowedKeys = ['Backspace', 'Delete', 'Enter', 'Tab', 'ArrowLeft', 'ArrowRight'];
        if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
            e.preventDefault();
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            passwordInputRef.current?.focus();
        }
    };

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <Form<LoginFormValues>
            onFinish={handleLoginSubmit}
            className={styles.form}
            layout="vertical"
        >
            {/* Header */}
            <header className={styles.header}>
                <Link href={getLoginLink()}>
                    <a className={styles.title}>{t('auth.login.form.title')}</a>
                </Link>
            </header>

            {/* Telegram Login Button */}
            <button
                type="button"
                onClick={handleTelegramLogin}
                className={styles.socialButton}
            >
                <Image
                    src="/static/img/telegram.png"
                    alt="Telegram"
                    width={20}
                    height={20}
                />
                <span>{t('auth.login.social.telegram')}</span>
            </button>

            {/* Google Login Button */}
            <div className={styles.googleWrapper}>
                {isGoogleLoading ? (
                    <button
                        type="button"
                        disabled
                        className={styles.submitButton}
                    >
                        <BeatLoader color="#fff" size={10} />
                    </button>
                ) : (
                    <div className={styles.googleButtonShell}>
                        <div className={styles.googleButton} aria-hidden="true">
                            <Image
                                src="/static/svgs/google.svg"
                                alt="Google"
                                width={20}
                                height={20}
                            />
                            <span>{t('auth.login.social.google')}</span>
                        </div>
                        <div className={styles.googleNativeOverlay}>
                            <GoogleLogin
                                width="100%"
                                text="signin_with"
                                locale="uz"
                                onSuccess={handleGoogleLoginSuccess}
                                onError={handleGoogleLoginError}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Divider */}
            <Divider className={styles.divider}>{t('auth.login.divider')}</Divider>

            {/* Segment Toggle */}
            <Segmented
                value={segmentValue}
                onChange={(value) => setSegmentValue(value as 'email' | 'phone')}
                options={[
                    {
                        label: t('auth.login.segment.email'),
                        value: 'email',
                        icon: <AiOutlineMail />,
                    },
                    {
                        label: t('auth.login.segment.phone'),
                        value: 'phone',
                        icon: <AiOutlinePhone />,
                    },
                ]}
                block
                className={styles.segment}
            />

            {/* Email/Phone Input */}
            {segmentValue === 'email' ? (
                <Form.Item
                    name="email"
                    rules={[
                        { required: true, message: t('auth.login.validation.email.required') },
                        { type: 'email', message: t('auth.login.validation.email.invalid') },
                    ]}
                >
                    <Input
                        size="large"
                        addonBefore={<AiOutlineMail size={20} />}
                        type="email"
                        placeholder={t('auth.login.placeholders.email')}
                        onKeyDown={handleEmailKeyDown}
                        autoComplete="email"
                    />
                </Form.Item>
            ) : (
                <Form.Item
                    name="phone"
                    rules={[
                        { required: true, message: t('auth.login.validation.phone.required') },
                        { pattern: /^\d{9}$/, message: t('auth.login.validation.phone.invalid') },
                    ]}
                >
                    <Input
                        size="large"
                        type="text"
                        placeholder={t('auth.login.placeholders.phone')}
                        maxLength={9}
                        onKeyDown={handlePhoneKeyDown}
                        addonBefore="+998"
                        autoComplete="tel"
                    />
                </Form.Item>
            )}

            {/* Password Input */}
            <Form.Item
                name="password"
                rules={[{ required: true, message: t('auth.login.validation.password.required') }]}
                hasFeedback
            >
                <Input.Password
                    size='large'
                    prefix={<AiOutlineLock size={20} className={styles.lockIcon} />}
                    placeholder={t('auth.login.placeholders.password')}
                    ref={passwordInputRef}
                />
            </Form.Item>

            {/* Submit Button */}
            <div className={styles.submitWrapper}>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className={styles.submitButton}
                >
                    {isSubmitting ? <BeatLoader color="#fff" size={10} /> : t('auth.login.submit')}
                </button>
            </div>

            {/* Forgot Password Link */}
            <p className={styles.forgotPassword}>
                <Link href="/auth/password/forgot">
                    <a className={styles.forgotPasswordLink}>
                        {t('auth.login.forgot_password')}
                    </a>
                </Link>
            </p>
        </Form>
    );
};

export default LoginForm;

